import React, { useState } from 'react';
import { EyeOffIcon, GlobeIcon, LockIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, accountNav } from '../../../components/layout/SubNav';
import { Card } from '../../../components/ui/Panels';
import { Avatar } from '../../../components/ui/Avatar';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Field, Input, Textarea } from '../../../components/ui/Field';
import { customer } from '../../../data/customer';
import { cuisineLabel } from '../../../data/taxonomy';
import { dateWithYear } from '../../../utils/format';
import { useLocale } from '../../../contexts/LocaleContext';
export function Profile() {
  const {
    locale
  } = useLocale();
  const [name, setName] = useState(customer.name);
  const [email, setEmail] = useState(customer.email);
  const [phone, setPhone] = useState(customer.phone);
  const [address, setAddress] = useState(customer.addressLine);
  const [city, setCity] = useState(customer.city);
  const [about, setAbout] = useState('Two adults and a nine-year-old. We eat late, except when she is at the table.');
  const [saved, setSaved] = useState(false);
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2400);
  };
  return <AppShell title="Account" subtitle={`Member since ${dateWithYear(customer.joinedOn, locale)}`} data-id="element-1649" data-mp="1mdl1gmb89t1u">
      <SubNav items={accountNav} data-id="element-1650" data-mp="1rc4qivl4bkyf" />

      <form onSubmit={save} className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-1651" data-mp="1rd7f2wuyxbb6">
        <div className="space-y-6" data-id="element-1652" data-mp="1ddoi907lxme6">
          <Card className="p-5" data-id="element-1653" data-mp="1depwfdjx6p04">
            <div className="flex items-center gap-2" data-id="element-1654" data-mp="1hdyrclqp4sx9">
              <GlobeIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1655" data-mp="1hei5rsh50z6j" />
              <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1656" data-mp="37rz6b6gdociw">Public</h2>
              <Badge tone="primary" data-id="element-1657" data-mp="3br80udj8sefi">Chefs can see this</Badge>
            </div>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1658" data-mp="okbm5mhx3lyo">Your name and photo appear on requests, reviews and in chat.</p>

            <div className="mt-4 flex flex-wrap items-center gap-4" data-id="element-1659" data-mp="jkfr4cr774v9">
              <Avatar name={name} src={customer.photo} size="lg" data-id="element-1660" data-mp="fj3xlw57dzhs" />
              <div data-id="element-1661" data-mp="fjds9x170utt">
                <Button type="button" variant="outline" size="sm" data-id="element-1662" data-mp="fkykvh3y63p1">
                  Change photo
                </Button>
                <p className="mt-1.5 text-[12px] text-ink-3" data-id="element-1663" data-mp="17atcxlj6w7he">JPG or PNG, at least 400 × 400.</p>
              </div>
            </div>

            <Field label="Display name" id="p-name" className="mt-5" data-id="element-1664" data-mp="139zngnq3mkme">
              <Input id="p-name" value={name} onChange={e => setName(e.target.value)} data-id="element-1665" data-mp="yhge62yg2bne" />
            </Field>

            <Field label="A line about your household" id="p-about" hint="optional" className="mt-4" data-id="element-1666" data-mp="thty7lr8ehaw">
              <Textarea id="p-about" rows={3} value={about} onChange={e => setAbout(e.target.value)} data-id="element-1667" data-mp="1qzc60e7bg51d" />
            </Field>
          </Card>

          <Card className="p-5" data-id="element-1668" data-mp="1m5hfaqtampss">
            <div className="flex items-center gap-2" data-id="element-1669" data-mp="1tj8jkx6z3mbp">
              <LockIcon className="h-4 w-4 text-ink-2" aria-hidden="true" data-id="element-1670" data-mp="1tkjlerguyk06" />
              <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1671" data-mp="1yil6fhxugtea">Private</h2>
              <Badge tone="neutral" data-id="element-1672" data-mp="23ftpdkqth1gl">
                <EyeOffIcon className="h-3 w-3" aria-hidden="true" data-id="element-1673" data-mp="23izev13cy5cu" /> Hidden until you book
              </Badge>
            </div>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1674" data-mp="2chul2d6ejfg5">
              Your address is shared with a chef only once you accept their quote. Contact details are never shown in search.
            </p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1675" data-mp="2m7e2deegv18w">
              <Field label="Email" id="p-email" data-id="element-1676" data-mp="2m7w76zf72nsf">
                <Input id="p-email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-id="element-1677" data-mp="2r6z41wl0tnpb" />
              </Field>
              <Field label="Phone" id="p-phone" data-id="element-1678" data-mp="2v9dmwgoowngl">
                <Input id="p-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} data-id="element-1679" data-mp="3075d969os1io" />
              </Field>
              <Field label="Address" id="p-address" className="sm:col-span-2" data-id="element-1680" data-mp="2mjwzp81w97xm">
                <Input id="p-address" value={address} onChange={e => setAddress(e.target.value)} data-id="element-1681" data-mp="2ij3926x2hf11" />
              </Field>
              <Field label="District & city" id="p-city" data-id="element-1682" data-mp="2wahs2dqz5g83">
                <Input id="p-city" value={city} onChange={e => setCity(e.target.value)} data-id="element-1683" data-mp="2wc2ok8wun98l" />
              </Field>
              <Field label="Postcode" id="p-post" data-id="element-1684" data-mp="23lkolgrf9nze">
                <Input id="p-post" defaultValue={customer.postcode} className="tnum" data-id="element-1685" data-mp="23m2ruxci52gt" />
              </Field>
            </div>

            <div className="mt-5 flex items-center gap-3" data-id="element-1686" data-mp="1zm24uobc7iy5">
              <Button type="submit" variant="action" data-id="element-1687" data-mp="2dks2c522jkn1">
                Save changes
              </Button>
              {saved && <span role="status" className="text-[13px] font-medium text-success" data-id="element-1688" data-mp="28mqgjcd9d77v">
                  Saved
                </span>}
            </div>
          </Card>
        </div>

        <aside className="space-y-4" data-id="element-1689" data-mp="3jzkgc4g467bk">
          <Card className="p-5" data-id="element-1690" data-mp="15sbauldrfoyc">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1691" data-mp="15r9xubd6osdz">Cuisines you book most</h2>
            <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-1692" data-mp="1asfsieqempq5">
              {customer.favouriteCuisineIds.map(id => <li key={id} className="rounded-full bg-oat px-3 py-1 text-[13px] text-ink-2" data-id="element-1693" data-mp="wtpv0xzoao19">
                  {cuisineLabel(id)}
                </li>)}
            </ul>
            <p className="mt-3 text-[12px] text-ink-3" data-id="element-1694" data-mp="1pi56jajlio7u">Used to order search results, never shown to chefs.</p>
          </Card>

          <Card className="p-5" data-id="element-1695" data-mp="1fq8yb6nsphpw">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1696" data-mp="1fp66mwinfb8x">What a chef sees</h2>
            <div className="mt-3 flex items-center gap-3 rounded-lg border border-hairline p-3" data-id="element-1697" data-mp="1ko0vaf3aru0y">
              <Avatar name={name} src={customer.photo} size="sm" data-id="element-1698" data-mp="28e4r40a8uphe" />
              <div className="min-w-0" data-id="element-1699" data-mp="2d64ww9czwclw">
                <p className="truncate text-sm font-medium text-ink" data-id="element-1700" data-mp="2d766e9ed4r1i">{name}</p>
                <p className="truncate text-[12px] text-ink-3" data-id="element-1701" data-mp="2n0k80zls5xs4">{city}</p>
              </div>
            </div>
            <p className="mt-3 text-[12px] text-ink-3" data-id="element-1702" data-mp="2i3trkb62d066">
              Plus your dietary record and kitchen profile — the two things they need to cook safely.
            </p>
          </Card>
        </aside>
      </form>
    </AppShell>;
}